<script setup>
import GlobalEmitter from '~/glxp/utils/emitter'

import { useContentful } from '~/composables/useContentful'
import query from '~/graphql/page-news'
import relatedQuery from '~/graphql/page-news-related'

const contentful = useContentful()
const route = useRoute()
const slug = computed(() => route.params.slug)

/**
 * Data fetching
 */
const { data, error } = await useAsyncData(async () => {
  const response = await contentful.graphql(query, { slug: slug.value })
  const json = await response.json()
  const { items, total } = json.data.newsCollection

  if (total < 1) {
    throw new Error('Page not found')
  }
  const page = items[0]

  const relatedResponse = await contentful.graphql(relatedQuery, {
    category: page.category,
    slug: slug.value,
  })
  const relatedJson = await relatedResponse.json()
  const { items: related } = relatedJson.data.newsCollection
  return { page, related }
})

watchEffect(() => {
  if (error.value) {
    throw createError({
      statusCode: 404,
      statusMessage: 'Page not found',
      fatal: true,
    })
  }
})

const page = computed(() => data.value?.page)
const related = computed(() => data.value?.related)

useHead({
  title: data.value?.page.title,
  titleTemplate: (title) => title && `${title} | Virgin Galactic`,
  meta: data.value.page?.seo && [
    { name: 'title', content: () => data.value.page.seo.title },
    { name: 'description', content: () => data.value.page.seo.description },
    data.value.page.seo.keywords && {
      name: 'keywords',
      content: () => data.value.page.seo.keywords.join(', '),
    },
    (data.value.seo.noindex || data.value.page.seo.nofollow) && {
      name: 'robots',
      content: () =>
        [
          data.value.page.seo?.noindex ? 'noindex' : null,
          data.value.page.seo?.nofollow ? 'nofollow' : null,
        ]
          .filter((value) => value !== null)
          .join(','),
    },
  ],
})

/**
 * Component imports
 */

const ModuleContentCarousel = resolveComponent('moduleContentCarousel')
const ModuleContentGrid = resolveComponent('ModuleContentGrid')
const ModuleFaq = resolveComponent('ModuleFAQ')
const ModuleImageGallery = resolveComponent('ModuleImageGallery')
const ModuleQuote = resolveComponent('ModuleQuote')
const ModuleText = resolveComponent('ModuleText')
const ModuleVideo = resolveComponent('ModuleVideo')
const components = {
  ModuleContentCarousel,
  ModuleContentGrid,
  ModuleFaq,
  ModuleImageGallery,
  ModuleQuote,
  ModuleText,
  ModuleVideo,
}

/**
 * Sections with dynamic components
 */
const sections = computed(() =>
  page.value.sectionsCollection?.items.map((item) => {
    const output = {
      component: components[item.__typename],
      attrs: item,
    }

    if (['ModuleFaq', 'ModuleContentGrid'].indexOf(item.__typename) > -1) {
      output.items = item.itemsCollection.items
    }

    return output
  })
)

/**
 * Anchors
 */
const el = ref(null)
const anchors = computed(() =>
  page.value.sectionsCollection?.items.filter(
    (item) => item.__typename === 'ModuleNavigationAnchor'
  )
)
const hasAnchors = computed(() => !!anchors.value?.length)

function scrollToAnchor(slug) {
  const scrollElement = el.value.querySelector(`#${slug}`)
  if (!scrollElement) {
    return
  }

  const bounds = scrollElement.getBoundingClientRect()
  const { top } = bounds
  const scrollTop =
    window.pageYOffset ||
    document.documentElement.scrollTop ||
    document.body.scrollTop

  window.scrollTo({
    top: top + scrollTop - 50,
    behavior: 'smooth',
  })
}

/**
 * Tags
 */
const tags = computed(() => page.value.contentfulMetadata.tags)

/**
 *  Meta
 */
useHead({
  title: page.value.title,
  titleTemplate: (title) => title && `${title} | Virgin Galactic`,
})

/**
 * Lifecycle
 */
onMounted(() => {
  GlobalEmitter.emit('webgl_raf_stop')
  GlobalEmitter.emit('webgl_loading_progress', { progress: 100 })
})
</script>

<template>
  <article
    v-if="page"
    class="page-news"
    :class="{ 'page-content--has-anchors': hasAnchors }"
    ref="el"
  >
    <div class="container">
      <PageHero
        class="page-news__hero"
        :title="page.title"
        :eyebrow="page.category || 'news'"
        :copy="page.copy"
        :image="[
          {
            src: page.heroImage.large,
            width: page.heroImage.width,
            height: page.heroImage.height,
          },
        ]"
        :date="page.date"
        :wide-text="true"
      />

      <ModularPageNavigation
        v-if="anchors?.length"
        :title="page.title"
        :anchors="anchors"
        @scroll-to-anchor="scrollToAnchor"
      />

      <div class="page-news__sections">
        <template v-for="section in sections">
          <div
            v-if="section.__typename === 'ModuleNavigationAnchor'"
            :id="section.slug"
          />
          <component
            v-else
            class="page-news__section"
            :key="section.attrs.sys.id"
            :is="section.component"
            v-bind="section.attrs"
          />
        </template>

        <template v-if="tags">
          <HorizontalRule center class="page-news__divider" />

          <div class="page-news__tags">
            <NuxtLink
              v-for="tag in tags"
              :key="tag.id"
              :to="`/news?tag=${tag.id}`"
              class="page-news__tag text__eyebrow-medium"
            >
              {{ tag.name }}
            </NuxtLink>
          </div>
        </template>
      </div>
    </div>
    <section class="page-news__related">
      <div class="page-news__related-eyebrow text__eyebrow-big">
        {{ $t('newsDetail.relatedEyebrow') }}
      </div>
      <div class="page-news__related-inner container">
        <NewsArticle
          v-for="item in related"
          v-bind="item"
          :key="item.sys.id"
          :image="item.heroImage"
          class="page-news__related-article"
        />
      </div>
    </section>
  </article>
</template>

<style lang="scss">
@import '~/styles/vendors/sass-mq/mq';
@import '~/styles/utils/variables';
@import '~/styles/utils/functions';

.page-news {
  background: url('~/assets/images/home/bg-stars-repeat.jpg') center top repeat;

  // glow
  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    z-index: 0;

    height: 1300px;

    background: radial-gradient(
        62.22% 94.81% at 99.13% 0%,
        rgba(200, 10, 251, 0.15) 0%,
        rgba(200, 10, 251, 0) 100%
      ),
      radial-gradient(
        50.05% 50.05% at 50% 0%,
        rgba(21, 42, 244, 0.22) 0%,
        rgba(21, 42, 244, 0) 100%
      ),
      linear-gradient(to top, rgba(13, 6, 29, 0) 0%, #0d061d 100%);
    transform: matrix(-1, 0, 0, 1, 0, 0);
  }

  > .container {
    position: relative;
    z-index: 1;
  }

  &__sections {
    padding: 0 0 163px;

    @include mq($from: 'l') using ($from) {
      padding: 0 0 125px;
    }
  }

  &__section {
    &:not(:first-child) {
      margin-top: 72px;
    }
    @include mq($from: 'l') using ($from) {
      margin-top: 125px;
      margin-bottom: 125px;
    }
  }

  &__divider {
    margin-top: 60px;
    margin-bottom: 60px;
  }

  &__tags {
    display: flex;
    justify-content: center;
    flex-wrap: wrap;

    @include mq($from: 'l') using ($from) {
      margin: 125px 0 0;
    }
  }

  &__tag {
    color: $brand-white;
    background: $brand-purple;
    height: 46px;
    padding: 0 26px;
    margin: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 23px;
    white-space: nowrap;
  }

  &__related {
    background: radial-gradient(
        148.16% 141.61% at 59.41% 0%,
        rgba(13, 6, 29, 0) 0%,
        #0d061d 100%
      ),
      radial-gradient(
        83.81% 83.81% at 27.4% -14.02%,
        rgba(178, 79, 205, 0.15) 0%,
        rgba(200, 10, 251, 0) 100%
      ),
      radial-gradient(
        66.88% 74.42% at 103.68% -30.74%,
        rgba(21, 42, 244, 0.22) 0%,
        rgba(21, 42, 244, 0) 100%
      ),
      #0c011c;
    padding: 72px 0;

    @include mq($from: 'l') using ($from) {
      padding: 125px 0;
    }
  }

  &__related-inner {
    @include mq($from: 'l') using ($from) {
      display: flex;
      justify-content: center;
    }
  }

  &__related-eyebrow {
    display: block;
    text-align: center;
    margin-bottom: 66px;
  }

  &__related-article {
    margin-bottom: 32px;
    @include mq($from: 'l') using ($from) {
      margin-bottom: 0;
      width: gs(6, 5, 'l');
      &:not(:first-child) {
        margin-left: gs(0, 1, 'l');
      }
    }
  }

  &--has-anchors {
    .page-news__sections::after {
      content: '';
      display: block;
      height: 50px;
      margin-bottom: 125px;
    }
  }
}
</style>
